import {
  DefinitionNode,
  DirectiveNode,
  DocumentNode,
  GraphQLArgument,
  GraphQLField,
  GraphQLInputField,
  GraphQLNamedType,
  GraphQLNonNull,
  GraphQLSchema,
  GraphQLType,
  OperationTypeNode,
  TypeDefinitionNode,
  TypeExtensionNode,
} from 'graphql';
import { difference } from 'lodash';

export type GraphQLNode =
  | GraphQLNamedType
  | GraphQLArgument
  | GraphQLArgumentNode;
export type GraphQLArgumentNode =
  | GraphQLField<unknown, unknown>
  | GraphQLInputField;

const builtInScalar = new Set(['String', 'Int', 'Float', 'Boolean', 'ID']);

export type RootTypeNames = Record<OperationTypeNode, string | null>;
export function getRootTypeNames(schema: GraphQLSchema): RootTypeNames {
  return {
    query: schema.getQueryType()?.name ?? null,
    mutation: schema.getMutationType()?.name ?? null,
    subscription: schema.getSubscriptionType()?.name ?? null,
  };
}

export const unwrapNonNullType = (
  node: GraphQLNode | GraphQLType,
): GraphQLType => {
  const innerNode = 'type' in node ? node.type : node;
  if (innerNode instanceof GraphQLNonNull) {
    return innerNode.ofType;
  }
  return innerNode;
};

export const unwrapType = (node: GraphQLNode): GraphQLNamedType => {
  if ('type' in node) {
    return unwrapNestedType(node.type);
  }
  if ('ofType' in node) {
    return unwrapNestedType(node);
  }
  return node;
};

export function unwrapNestedType(type: GraphQLType): GraphQLNamedType {
  let currentType: GraphQLType = type;
  while ('ofType' in currentType) {
    currentType = currentType.ofType;
  }
  return currentType;
}

export function getTypesAndFields(documentNode: DocumentNode) {
  const typeDefinitions = documentNode.definitions
    .filter(isTypeDefinitionOrExtensionNode)
    .filter(
      (type) =>
        !type.name.value.startsWith('__') &&
        !builtInScalar.has(type.name.value),
    );

  const fields = typeDefinitions.flatMap(
    (typeDefinition) =>
      ('fields' in typeDefinition &&
        typeDefinition.fields?.map(
          (field) => `${typeDefinition.name.value}.${field.name.value}`,
        )) ||
      [],
  );

  const types = typeDefinitions.map((type) => type.name.value);

  return {
    types,
    fields,
  };
}

/**
 * Finds any directives matching the directiveName on schema definition or
 * schema extension nodes.
 * @returns An array of DirectiveNode matching directiveName.
 */
export function getSchemaDirectives(
  documentNode: DocumentNode,
  directiveName: string,
): DirectiveNode[] {
  return documentNode.definitions.flatMap((def) =>
    def.kind === 'SchemaDefinition' || def.kind === 'SchemaExtension'
      ? (def.directives?.filter(
          (directive) => directive.name.value === directiveName,
        ) ?? [])
      : [],
  );
}

/**
 * Whether the given schema definition has a link directive which imports
 * connect.
 */
export function hasConnectLink(documentNode: DocumentNode) {
  return getSchemaDirectives(documentNode, 'link').some((linkDirective) => {
    const urlArg = linkDirective.arguments?.find(
      (arg) => arg.name.value === 'url',
    )?.value;

    return (
      urlArg?.kind === 'StringValue' &&
      urlArg.value.startsWith('https://specs.apollo.dev/connect/v')
    );
  });
}

export function countAddedAndDeletedTypes({
  baseDocumentNode,
  modifiedDocumentNode,
}: {
  baseDocumentNode: DocumentNode;
  modifiedDocumentNode: DocumentNode;
}) {
  const { types: baseTypes, fields: baseFields } =
    getTypesAndFields(baseDocumentNode);

  const { types: modifiedTypes, fields: modifiedFields } =
    getTypesAndFields(modifiedDocumentNode);

  return {
    typesAdded: difference(modifiedTypes, baseTypes).length,
    typesRemoved: difference(baseTypes, modifiedTypes).length,
    fieldsAdded: difference(modifiedFields, baseFields).length,
    fieldsRemoved: difference(baseFields, modifiedFields).length,
    baseSchemaTypeCount: baseTypes.length,
    baseSchemaFieldCount: baseFields.length,
    modifiedSchemaTypeCount: modifiedTypes.length,
    modifiedSchemaFieldCount: modifiedFields.length,
  };
}

const TYPE_DEFINITION_OR_EXTENSION_KINDS = new Set<DefinitionNode['kind']>([
  'ScalarTypeDefinition',
  'ObjectTypeDefinition',
  'InterfaceTypeDefinition',
  'UnionTypeDefinition',
  'EnumTypeDefinition',
  'InputObjectTypeDefinition',
  'ScalarTypeExtension',
  'ObjectTypeExtension',
  'InterfaceTypeExtension',
  'UnionTypeExtension',
  'EnumTypeExtension',
  'InputObjectTypeExtension',
]);

function isTypeDefinitionOrExtensionNode(
  node: DefinitionNode | undefined | null,
): node is TypeDefinitionNode | TypeExtensionNode {
  return !!node && TYPE_DEFINITION_OR_EXTENSION_KINDS.has(node.kind);
}
